import { FC, Suspense, lazy, useEffect, useRef, useState } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import hidePopover from '../../utils/hide-popover';
import './popover.scss';

const MobileMenu = lazy(() => import(/* webpackChunkName: "mobile-menu" */ './components/mobile-menu'));

type PopoverProps = RouteComponentProps & {
    onClose: () => boolean;
    onShown: () => boolean;
    trigger?: string;
    disableMobile: boolean;
    className?: string;
    menu: Element;
    mobileTitle: Element;
    triggerAs: Element;
    hoverABTest?: boolean;
    disabled?: boolean;
};

const Popover: FC<PopoverProps> = (props) => {
    const {
        location,
        mobileTitle,
        triggerAs,
        menu,
        disableMobile,
        className,
        trigger = 'click',
        onShown = () => true,
        onClose = () => true,
        disabled
    } = props;
    const wrapperRef = useRef<HTMLDivElement>(null);
    const url = location.pathname + location.search;
    const ref = useRef<HTMLDivElement>(null);
    const [toggleMobile, setMobile] = useState(false);
    const [desktopInteraction, setDesktopInteraction] = useState(false);

    useEffect(() => {
        hidePopover();
    }, [url]);

    const handleClick = (e: Event) => {
        const popupVisible = wrapperRef.current!.style.display !== 'none';

        if (!popupVisible) {
            document.addEventListener('click', handleOutsideClick);
        } else {
            document.removeEventListener('click', handleOutsideClick);
        }

        if (!desktopInteraction) {
            setDesktopInteraction(true);
        }

        changeDisplay();

        e.preventDefault();
    };

    const triggerTypes = {
        click: {
            onClick: handleClick
        },
        hover: {
            onClick: (e: { preventDefault: () => void }) => e.preventDefault(),
            onMouseEnter: () => handleHover(true),
            onMouseLeave: () => handleHover(false)
        }
    };

    const changeDisplay = () => {
        let { display } = wrapperRef.current!.style;

        if (display === 'none') {
            display = 'block';
            onShown();
        } else {
            display = 'none';
            onClose();
        }

        wrapperRef.current!.style.display = display;
    };

    const handleHover = (visible: boolean) => {
        if (visible) {
            // @ts-ignore
            const popovers = [...document.getElementsByClassName('lmp-popover-wrapper')];

            popovers.forEach((element) => {
                // eslint-disable-next-line
                element.style.display = 'none';
            });
        }

        if (!desktopInteraction) {
            setDesktopInteraction(true);
        }

        changeDisplay();
    };

    const handleOutsideClick = (e: Event) => {
        const popupHidden = wrapperRef.current?.style?.display === 'none';

        // @ts-ignore
        if (!ref.current || ref.current.contains(e.target) || popupHidden) {
            return;
        }

        handleClick(e);
    };
    const classNameBlock = disableMobile ? 'd-block' : 'd-none d-lg-block';

    return (
        <div className='lmp-popover-container' ref={ref}>
            <div className={`${classNameBlock} ${className}`}>
                <div {...triggerTypes[trigger]}>{triggerAs}</div>
                <div className='lmp-popover-wrapper' ref={wrapperRef} style={{ display: 'none' }}>
                    <div className='arrow-container'>
                        <span className={`arrow-up ${classNameBlock}`} />
                    </div>
                    {(desktopInteraction || trigger === 'hover') && !disabled && (
                        <div className='lmp-popover'>
                            <>{menu}</>
                        </div>
                    )}
                </div>
            </div>
            {!disableMobile && (
                <div className='d-xs-flex d-lg-none'>
                    <div onClick={() => setMobile(true)}>
                        <>{triggerAs}</>
                    </div>
                    {toggleMobile && !disableMobile && (
                        <Suspense fallback={<div />}>
                            <MobileMenu MenuElement={menu} MobileTitle={mobileTitle} toggleMobile={setMobile} />
                        </Suspense>
                    )}
                </div>
            )}
        </div>
    );
};

export default withRouter(Popover);
